import { Plus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useCreateLocalCustomerMutation } from "@/api/query-hooks";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function LocalCustomerCreateDialog({ onCreated }: { onCreated: (clientId: number) => void }) {
  const { t } = useTranslation("licensing");
  const mutation = useCreateLocalCustomerMutation();
  const [open, setOpen] = useState(false);
  const [number, setNumber] = useState("");
  const [name, setName] = useState("");

  const reset = () => {
    setNumber("");
    setName("");
  };

  return (
    <Dialog open={open} onOpenChange={(next) => {
      setOpen(next);
      if (!next && !mutation.isPending) reset();
    }}>
      <DialogTrigger asChild>
        <Button type="button"><Plus className="h-4 w-4" />{t("customers.create")}</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("customers.createTitle")}</DialogTitle>
          <DialogDescription>{t("customers.createDescription")}</DialogDescription>
        </DialogHeader>
        <form className="grid gap-4" onSubmit={(event) => {
          event.preventDefault();
          const customerNumber = number.trim();
          const customerName = name.trim();
          if (!customerNumber || !customerName || mutation.isPending) return;
          mutation.mutate({ number: customerNumber, name: customerName, requestId: crypto.randomUUID() }, {
            onSuccess: (customer) => {
              toast.success(t("customers.created"));
              setOpen(false);
              reset();
              onCreated(customer.id);
            },
            onError: (error) => toast.error(error instanceof Error ? error.message : t("customers.updateFailed"))
          });
        }}>
          <div className="grid gap-2">
            <Label htmlFor="local-customer-number">{t("customers.number")}</Label>
            <Input id="local-customer-number" value={number} maxLength={120} autoComplete="off" onChange={(event) => setNumber(event.target.value)} autoFocus />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="local-customer-name">{t("customers.name")}</Label>
            <Input id="local-customer-name" value={name} maxLength={200} autoComplete="off" onChange={(event) => setName(event.target.value)} />
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="ghost" disabled={mutation.isPending} onClick={() => setOpen(false)}>{t("customers.cancel")}</Button>
            <Button type="submit" disabled={mutation.isPending || !number.trim() || !name.trim()}>{t("customers.save")}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
